<script setup lang="ts">
/**
 * Wind Speed Legend Component
 * Shows a gradient bar mapping wind speed to particle colors.
 */
import { computed } from "vue";
import { Wind } from "@lucide/vue";
import { getWindColor } from "@/utils/windColorScale";
import { useUnitConversion } from "@/composables/useUnitConversion";
import type { TemperatureUnit } from "@/stores/settings.store";

const props = defineProps<{ unit: TemperatureUnit }>();

const { convertSpeed, labels: unitLabels } = useUnitConversion(
  () => props.unit,
);

// Sample speeds at key color stops to build the gradient (always in mph for color mapping)
const speedStops = [0, 5, 10, 15, 20, 25, 30, 40, 50];

const gradientStops = speedStops.map((speed, i) => {
  const percent = (i / (speedStops.length - 1)) * 100;
  return `${getColor(speed)} ${percent}%`;
});

const gradient = `linear-gradient(to right, ${gradientStops.join(", ")})`;

const tickLabels = computed(() => [
  { text: "Calm", position: "0%" },
  { text: `${convertSpeed(25)}`, position: "55%" },
  { text: `${convertSpeed(50)}+`, position: "100%" },
]);

const speedUnit = computed(() => unitLabels.value.speed);

/** Resolve the wind color for a speed in miles per hour. */
function getColor(speedMph: number): string {
  const c = getWindColor(speedMph);
  return `rgb(${c.r}, ${c.g}, ${c.b})`;
}
</script>

<template>
  <div
    class="flex w-fit items-center gap-2 rounded-panel border border-app-border bg-app-material px-4 py-2"
  >
    <Wind class="h-3.5 w-3.5 shrink-0 text-text-primary" />
    <span class="text-xs font-medium whitespace-nowrap text-text-primary"
      >Wind</span
    >
    <div class="flex flex-col gap-0.5">
      <div class="h-2.5 w-30 rounded-[5px]" :style="{ background: gradient }" />
      <div class="relative h-3 w-30">
        <span
          v-for="label in tickLabels"
          :key="label.text"
          class="absolute -translate-x-1/2 text-[9px] whitespace-nowrap text-text-muted first:translate-x-0 last:-translate-x-full"
          :style="{ left: label.position }"
          >{{ label.text }}</span
        >
      </div>
    </div>
    <span class="text-[10px] text-text-muted">{{ speedUnit }}</span>
  </div>
</template>
